import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Tab, Nav } from 'react-bootstrap';
import './MyAccount.css';
import Navbar from '../../Pages/Navbar/JobSeekerNavbar';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';
import { FaEye, FaEyeSlash, FaUser, FaLock, FaArrowLeft } from 'react-icons/fa';

function MyAccount() {
    const [activeTab, setActiveTab] = useState('profile');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile_number: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [userId, setUserId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user && user.id) {
            setUserId(user.id);
            fetchUserData(user.id);
        } else {
            Swal.fire({
                title: 'Error!',
                text: 'No user found. Please login again.',
                icon: 'error',
                confirmButtonText: 'OK'
            }).then(() => {
                window.location.href = '/login';
            });
        }
    }, []);

    const fetchUserData = async (id) => {
        try {
            const response = await fetch(`${BaseUrl}/${id}`);
            if (!response.ok) {
                throw new Error('Failed to fetch user data');
            }
            const userData = await response.json();

            setFormData({
                name: `${userData.first_name} ${userData.last_name}`,
                email: userData.email,
                mobile_number: userData.mobile_number,
                oldPassword: userData.password || '',
                newPassword: '',
                confirmPassword: ''
            });
            setLoading(false);
        } catch (error) {
            console.error('Error fetching user data:', error);
            Swal.fire({
                title: 'Error!',
                text: 'Failed to load user data',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            setLoading(false);
        }
    };

const handleChange = (e) => {
  const { name, value } = e.target;
  
  // Special handling for name field - allow only alphabets and spaces
  if (name === 'name') {
    // Allow only letters (A-Z, a-z) and spaces
    const filteredValue = value.replace(/[^A-Za-z\s]/g, '');
    
    // Prevent multiple consecutive spaces
    const finalValue = filteredValue.replace(/\s+/g, ' ').trimStart();
    
    setFormData({
      ...formData,
      [name]: finalValue
    });
  } else {
    setFormData({
      ...formData,
      [name]: value
    });
  }
};

    const togglePassword = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleUpdate = async () => {
        try {
            const response = await fetch(`${BaseUrl}/${userId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    first_name: formData.name.split(' ')[0],
                    last_name: formData.name.split(' ')[1] || '',
                    mobile_number: formData.mobile_number,
                    password: formData.oldPassword // keep old password when updating profile
                }),
            });

            if (!response.ok) {
                throw new Error('Update failed');
            }

            Swal.fire({
                title: 'Success!',
                text: 'Profile updated successfully!',
                icon: 'success',
                confirmButtonText: 'OK'
            });
        } catch (error) {
            console.error('Update error:', error);
            Swal.fire({
                title: 'Error!',
                text: 'Failed to update profile',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

const handlePasswordUpdate = async () => {
  if (formData.newPassword.trim() === '') {
    Swal.fire({
      title: 'Error!',
      text: 'New password cannot be empty!',
      icon: 'error',
      confirmButtonText: 'OK'
    });
    return;
  }

  // Add password length validation
  if (formData.newPassword.length < 8) {
    Swal.fire({
      title: 'Error!',
      text: 'Password must be at least 8 characters long!',
      icon: 'error',
      confirmButtonText: 'OK'
    });
    return;
  }

  if (formData.newPassword !== formData.confirmPassword) {
    Swal.fire({
      title: 'Error!',
      text: 'Passwords do not match!',
      icon: 'error',
      confirmButtonText: 'OK'
    });
    return;
  }

  try {
    const response = await fetch(`${BaseUrl}/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: formData.email,
        first_name: formData.name.split(' ')[0],
        last_name: formData.name.split(' ')[1] || '',
        mobile_number: formData.mobile_number,
        password: formData.newPassword,
      }),
    });

    if (!response.ok) throw new Error('Password update failed');

    const result = await response.json();

    // Show success alert immediately
    await Swal.fire({
      title: 'Success!',
      text: 'Password changed successfully!',
      icon: 'success',
      confirmButtonText: 'OK'
    });

    // Clear password fields after successful change
    setFormData(prev => ({
      ...prev,
      oldPassword: formData.newPassword,
      newPassword: '',
      confirmPassword: ''
    }));

  } catch (error) {
    console.error('Password update error:', error);
    Swal.fire({
      title: 'Error!',
      text: 'Failed to change password',
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }
};

    // Back button handler
    const handleBack = () => {
        // You can customize where to redirect back to
        window.history.back(); // Goes back to previous page
        // OR use: window.location.href = '/dashboard'; // Redirect to specific page
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <Container className="py-4 card myaccount-card-body text-center">
                    <p>Loading user data...</p>
                </Container>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div style={{ marginTop: '80px' }}>
                <Container className="profile-settings-container mt-4 mb-5">
                    {/* Desktop Back Button - Top Left */}
                    <div className="d-none d-md-block mb-4">
                        <Button 
                            variant="outline-secondary" 
                            onClick={handleBack}
                            className="d-flex align-items-center gap-2"
                            style={{
                                border: '1px solid #ccc',
                                padding: '8px 16px',
                                borderRadius: '0px',
                                backgroundColor: 'white'
                            }}
                        >
                            <FaArrowLeft /> Back
                        </Button>
                    </div>

                    {/* Mobile Back Button - Top Fixed */}
                    <div className="d-md-none mb-4">
                        <Button 
                            variant="outline-secondary" 
                            onClick={handleBack}
                            className="d-flex align-items-center justify-content-center gap-2 w-100"
                            style={{
                                border: '1px solid #ccc',
                                padding: '10px 16px',
                                borderRadius: '0px',
                                backgroundColor: 'white',
                                fontSize: '16px'
                            }}
                        >
                            <FaArrowLeft /> Go Back
                        </Button>
                    </div>

                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        <Nav variant="tabs" className="custom-tabs justify-content-center mb-4">
                            <Nav.Item>
                                <Nav.Link eventKey="profile" style={{ width: '100%' }}>
                                    <FaUser /> Profile
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="password" style={{ width: '100%' }}>
                                    <FaLock /> Change Password
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content>
                            <Tab.Pane eventKey="profile">
                                <Form>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group className="myaccount-form-group">
                                                <Form.Label className="myaccount-label">Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={formData.name}
                                                    name="name"
                                                    className="myaccount-input"
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="myaccount-form-group">
                                                <Form.Label className="myaccount-label">Mobile No.</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={formData.mobile_number}
                                                    name="mobile_number"
                                                    className="myaccount-input"
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Group className="myaccount-form-group">
                                                <Form.Label className="myaccount-label">Email</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    value={formData.email}
                                                    name="email"
                                                    className="myaccount-input"
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="text-center">
                                        <button className="myaccount-submit-btn" onClick={handleUpdate}>
                                            UPDATE
                                        </button>
                                        
                                        {/* Additional Back Button at Bottom for Mobile */}
                                        <div className="d-md-none mt-3">
                                            <Button 
                                                variant="light" 
                                                onClick={handleBack}
                                                className="d-flex align-items-center justify-content-center gap-2 w-100"
                                                style={{
                                                    border: '1px solid #ddd',
                                                    padding: '8px 16px',
                                                    borderRadius: '0px'
                                                }}
                                            >
                                                <FaArrowLeft /> Back
                                            </Button>
                                        </div>
                                    </div>
                                </Form>
                            </Tab.Pane>

                            <Tab.Pane eventKey="password">
                                <Form>
                                    <div className="text-center mb-4">
                                        <img src="https://cdn-icons-png.flaticon.com/512/3064/3064197.png" width="80" alt="lock" />
                                    </div>

                                    <Form.Group className="mb-3 position-relative">
                                        <Form.Label className="myaccount-label">Current Password</Form.Label>
                                        <Form.Control
                                            type={showPasswords.current ? "text" : "password"}
                                            name="oldPassword"
                                            value={formData.oldPassword}
                                            onChange={handleChange}
                                            className="myaccount-input"
                                            placeholder="Current Password"
                                            readOnly
                                        />
                                        <span
                                            className="position-absolute"
                                            onClick={() => togglePassword('current')}
                                            style={{ top: '38px', right: '10px', cursor: 'pointer' }}
                                        >
                                            {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </Form.Group>

                                    <Form.Group className="mb-3 position-relative">
                                        <Form.Label className="myaccount-label">New Password</Form.Label>
                                        <Form.Control
                                            type={showPasswords.new ? "text" : "password"}
                                            name="newPassword"
                                            value={formData.newPassword}
                                            onChange={handleChange}
                                            className="myaccount-input"
                                            placeholder="Enter New Password"
                                        />
                                        <span
                                            className="position-absolute"
                                            onClick={() => togglePassword('new')}
                                            style={{ top: '38px', right: '10px', cursor: 'pointer' }}
                                        >
                                            {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </Form.Group>

                                    <Form.Group className="mb-4 position-relative">
                                        <Form.Label className="myaccount-label">Confirm Password</Form.Label>
                                        <Form.Control
                                            type={showPasswords.confirm ? "text" : "password"}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="myaccount-input"
                                            placeholder="Confirm New Password"
                                        />
                                        <span
                                            className="position-absolute"
                                            onClick={() => togglePassword('confirm')}
                                            style={{ top: '38px', right: '10px', cursor: 'pointer' }}
                                        >
                                            {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </Form.Group>

                                    <div className="text-center">
                                        <button className="myaccount-submit-btn" onClick={handlePasswordUpdate}>
                                            Change Password
                                        </button>
                                        
                                        {/* Additional Back Button at Bottom for Mobile */}
                                        <div className="d-md-none mt-3">
                                            <Button 
                                                variant="light" 
                                                onClick={handleBack}
                                                className="d-flex align-items-center justify-content-center gap-2 w-100"
                                                style={{
                                                    border: '1px solid #ddd',
                                                    padding: '8px 16px',
                                                    borderRadius: '0px'
                                                }}
                                            >
                                                <FaArrowLeft /> Back
                                            </Button>
                                        </div>
                                    </div>
                                </Form>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Container>
            </div>
        </>
    );
}

export default MyAccount;
